import {processDescendents} from '@megaphonenpm/mputils/src/DOM.js';
import {isNil, notNil} from '@megaphonenpm/mputils/src/utils.js';
import {isEqual} from 'lodash-es';

// The attribute used by Google Web Designer to track the current timeline label for a given element
const currentLabelAttr = 'data-gwd-current-label';

// A map of animations that have been queued for playback by playSequentially(). Map entries
// are pairs of the format: key=timeoutID, value=nodes. This map is used to terminate pending
// animations for a set of nodes if a new animation is requested for that set.
let pendingSequentialAnimations = new Map();

/**
 * Plays the animation specified by "label" on the specified node. Internally,
 * to execute an animation, the play() function removes the animation's
 * most recent CSS animation class ("label"), then waits until the DOM updates
 * (via setTimeout()), then applies the new specified label.
 *
 * @param node
 * @param label
 * @param animationDelay
 */
export function play (node, label, animationDelay) {
  if (isNil(node) || isNil(label)) {
    return;
  }

  if (notNil(animationDelay)) {
    node.style.animationDelay = animationDelay + 'ms';
  } else {
    node.style.removeProperty('animation-delay');
  }

  removeCurrentLabel(node);
  // Note: If the node's previous and new label are the same, reflow must be triggered, otherwise
  //       the animation won't play again (even if reflow has been triggered already for other nodes).
  //       Each and every node needs its own reflow trigger.
  triggerReflow(node);
  setLabel(node, label);
}

function removeCurrentLabel (node) {
  let currentLabel = node.getAttribute(currentLabelAttr);
  if (notNil(currentLabel)) {
    node.classList.remove(currentLabel);
    node.removeAttribute(currentLabelAttr);
  }
}

function setLabel (node, label) {
  if (notNil(label)) {
    node.classList.add(label);
    node.setAttribute(currentLabelAttr, label);
  }
}

function triggerReflow (node) {
  void(node.offsetHeight);
}

/**
 * Plays the specified animation on all descendents of the specified node EXCEPT for those descendents
 * that define an attribute named conditionAttrName with with the value conditionAttrVal.
 * The conditionAttrName attribute gives nodes the option to opt out of being played.
 *
 * Example:
 *   // Play all descendents of "this.main" except those nodes with the attribute 'data-no-scene-animations'
 *   // set to the value 'true'.
 *   deepPlay(this.main, 'start', 'data-no-scene-animations', 'true');
 *
 * @param node The root node whose descendents will play.
 * @param label The label to play.
 * @param conditionAttrName The name of an attribute specifying the condition under which to NOT play.
 * @param conditionAttrVal The value of an attribute specifying the condition under which to NOT play.
 */
export function deepPlay (node, label, conditionAttrName, conditionAttrVal) {
  processDescendents(node,
    (node, label) => {
          if (isNil(conditionAttrName) || isNil(conditionAttrVal)) {
            play(node, label);
          } else if (node.getAttribute(conditionAttrName) !== conditionAttrVal) {
            play(node, label);
          }
        },
    [label],
    Node.ELEMENT_NODE)
}

/**
 * Plays an animation on the set of elements specified by nodes.
 *
 * @param nodes The elements to play in sequence.
 * @param label The label of the animation to play (a CSS class)
 * @param groupDelay The number of ms to delay befor beginning the sequence.
 * @param itemDelay The number of ms to delay between playing each animation in the sequence.
 * @param sequenceStartCallback A function invoked after the play() function completes for all elements.
 *
 * @return number An animation ID that can be used to cancel the animation until groupDelay elapses.
 */
export function playSequentially (nodes, label, groupDelay, itemDelay, sequenceStartCallback) {
  if (isNil(nodes) || isNil(label)) {
    return;
  }

  // Automatic protective measure: if an animation is already pending for the specified nodes,
  // cancel the pending animation. This prevents flickering in animations that are requested
  // repeatedly before groupDelay elapses, without requiring the caller to track whether the
  // animation is still pending via the animation ID.
  pendingSequentialAnimations.forEach((value, key) => {
    if (isEqual(value, nodes)) {
      cancelSequentialPlay(key);
    }
  });

  // doAnimations(): Function to begin the requested animations
  let doAnimations = (animationID) => {
    nodes.forEach((node, index) => {
      play(node, label, itemDelay*index);
    });
    // All animations for all nodes have been queued, so inform the caller that the
    // playSequentially() request has now completed.
    if (notNil(sequenceStartCallback) && typeof sequenceStartCallback === 'function') {
      sequenceStartCallback(animationID);
    }
  };

  // Execute doAnimations()
  if (notNil(groupDelay)) {
    // Start the animation sequence after groupDelay ms elapses
    let timeoutID = setTimeout(() => {
      doAnimations(timeoutID);
      pendingSequentialAnimations.delete(timeoutID);
    }, groupDelay);
    pendingSequentialAnimations.set(timeoutID, nodes);
    // Return the timeoutID (as an "animation ID") so it can be used by the caller
    // to abort the animation (via cancelSequentialPlay()) if desired.
    return timeoutID;
  } else {
    // No groupDelay, so start the animation sequence immediately
    doAnimations(-1);
    return -1;
  }
}

/**
 * Aborts the pending animation specified by id.
 *
 * @param id An animation ID returned by playSequentially().
 */
export function cancelSequentialPlay (id) {
  clearTimeout(id);
  pendingSequentialAnimations.delete(id);
}

export function pause (node) {
    console.log('pausing: ' + node)
    node.style.animationPlayState = 'paused';
    node.style.webkitAnimationPlayState = 'paused';  // Required for Safari
  }

export function deepPause (node) {
    console.log('deep pausing: ' + node);
    processDescendents(node, pause, null, Node.ELEMENT_NODE);
  }
